/**
 * THE DECLARED EXHIBIT — the computation picks the exhibit, the canvas draws it.
 *
 * The server declares one exhibit from typed operations and dependency edges.
 * Figures name binding placeholders; values stay at the single server fence.
 * Declaration reads typed operations and bindings, never question text.
 * Undeclarable results return null and preserve the existing canvas behavior.
 */
import type { AskKpiGroup } from './declared-exhibit-groups';
import type {
  AskHelixNumericBindingDto,
  AskHelixRequestedOutputOutcomeDto,
} from '../../ask-helix';
import type { AskExhibitGeometry } from './declared-exhibit-geometry';
import { declareMeasureTable, type AskDecisionTableBody } from './declared-measure-table';
import { declarePeriodTable } from './declared-period-table';
import type { AskStoryline } from './declared-storyline';
import type { RuntimeV3ClauseCarrier } from '../../runtime-v3/resolved-meaning';
/** The closed exhibit registry, shared by the API (emits) and the web (draws). */
export const ASK_EXHIBIT_KINDS = [
  'kpi',
  'ranked_bars',
  'bridge',
  'decision_table',
  'trend',
  'composition',
  'target_gauge',
  'scenario_cards',
  'scenario_option_set',
  'status_board',
  'network',
  'table',
] as const;
export type AskExhibitKind = (typeof ASK_EXHIBIT_KINDS)[number];
/** One labelled figure. `figure` is a binding placeholder, never a value. */
export type AskExhibitFigure = Readonly<{ label: string; figure: string;
  periodBasis?: import('../../figure-period-basis').FigurePeriodBasis }>;
/**
 * A figure the engine DISCLOSES beside a lead (claimRole 'disclosure'), e.g.
 * customer-overlap commitments tracked outside a committed total. Rendered as
 * a secondary group, never summed with the lead. `direction` is computed by the
 * server from the bound value's sign, so the page styles a dis-synergy or risk
 * as a loss without ever reading a raw value.
 */
export type AskExhibitDisclosure = AskExhibitFigure & Readonly<{ direction: 'gain' | 'loss' | 'neutral' }>;
/** Value capture (2026-09-26): one ordered bridge step, e.g. a moved commitment and its governed reason. */
export type AskBridgeStep = AskExhibitFigure &
  Readonly<{
    reason: string;
    /**
     * The governed member the step moves (a commitment ref), so a step can open that member's
     * evidence (ev-it `commitmentId`). A link reads this ref, never the step's label text.
     */
    memberRef?: string;
  }>;
/**
 * The roles a scenario option reports (the 12%-capacity question: three
 * options, each with FTE, cut share, savings, protected seats by reason,
 * impact per priority, reinvestment and net savings).
 */
export const ASK_SCENARIO_OPTION_ROLES = [
  'fte', 'headcount', 'cut_share', 'savings', 'one_time_cost', 'protected_seats',
  'reinvestment', 'net_savings', 'priority_impact',
] as const;
export type AskScenarioOptionRole = (typeof ASK_SCENARIO_OPTION_ROLES)[number];

/** Why a seat is protected; closed so the canvas never matches on prose. */
export const ASK_PROTECTION_REASONS = ['ai_cyber', 'top8_priority', 'overlap'] as const;
export type AskProtectionReason = (typeof ASK_PROTECTION_REASONS)[number];

/**
 * The binding `coordinates` keys an option-set declaration reads (helix-51,
 * 2026-09-24 05:33 PT): one typed mechanism shared with the what-if slip.
 * Option, role, reason and priority are read from these keys by equality —
 * never parsed out of a label.
 */
export const ASK_SCENARIO_OPTION_COORDINATES = {
  option: 'option',
  role: 'role',
  reason: 'reason',
  priority: 'priority',
} as const;

export type AskScenarioOptionFigure = Readonly<{
  role: AskScenarioOptionRole;
  label: string;
  figure: string;
  reason?: AskProtectionReason;
  /** Governed priority ref, for `priority_impact` figures. */
  priority?: string;
}>;

export type AskScenarioOption = Readonly<{
  optionRef: string;
  label: string;
  description: string;
  /**
   * The governed rule that recommends this option, or null. Never set by
   * the canvas or by a largest-number heuristic: no rule, no emphasis.
   */
  recommendedBy: string | null;
  figures: readonly AskScenarioOptionFigure[];
}>;

/**
 * A status board's colours are the GOVERNED classification's own statuses
 * (`runtime-v3/classify-against-policy.ts`: target, expected path and an
 * at-risk tolerance declared by policy). The canvas maps each to red, amber,
 * green or grey; it never decides a status itself.
 */
export const ASK_STATUS_BOARD_STATUSES = [
  'on_track', 'at_risk', 'off_track', 'measurement_gap',
] as const;
export type AskStatusBoardStatus = (typeof ASK_STATUS_BOARD_STATUSES)[number];

export type AskStatusBoardItem = Readonly<{
  label: string;
  status: AskStatusBoardStatus;
  /** The governed owner, or null when the portfolio has no owner link yet. */
  owner: string | null;
  actual: string | null;
  target: string | null;
}>;

export type AskDeclaredExhibitBody =
  | Readonly<{
      kind: 'kpi';
      figures: readonly AskExhibitFigure[];
      /** Present only when the engine disclosed figures beside the lead. */
      disclosures?: readonly AskExhibitDisclosure[];
      /** Disclosed figures bound to exactly 0: owned here, drawn as one "N at $0" line, not as rows. */
      zeroDisclosures?: readonly AskExhibitFigure[];
      /**
       * #9052 B1: labelled rows by TYPED unit ("Annual run-rate", "To date"); they partition
       * `figures` exactly (askKpiGroupProblems). Absent: one ungrouped row.
       */
      groups?: readonly AskKpiGroup[];
    }>
  | Readonly<{ kind: 'ranked_bars'; subject: string | null; members: readonly AskExhibitFigure[] }>
  | Readonly<{
      kind: 'bridge';
      subject: string;
      from: AskExhibitFigure;
      to: AskExhibitFigure;
      change: AskExhibitFigure;
      /** The ordered steps from `from` to `to` (they sum to `change`); absent on a two-point bridge. */
      steps?: readonly AskBridgeStep[];
    }>
  | AskDecisionTableBody
  | Readonly<{ kind: 'trend'; subject: string | null; points: readonly AskExhibitFigure[]; change?: AskExhibitFigure }>
  | Readonly<{
      kind: 'scenario_option_set';
      subject: string;
      options: readonly AskScenarioOption[];
      /**
       * Figures that hold for the whole plan, not one option (e.g. protected
       * seats by reason): their binding carries no `option` coordinate.
       * Shown once, never repeated per option card.
       */
      planFigures: readonly AskScenarioOptionFigure[];
    }>
  | Readonly<{ kind: 'status_board'; subject: string; items: readonly AskStatusBoardItem[] }>
  | Readonly<{
      kind: 'composition' | 'target_gauge' | 'scenario_cards' | 'network' | 'table';
      outputIds: readonly string[];
    }>;

/**
 * THE STORYLINE CONTRACT — how much a partner says about each exhibit.
 *
 * `depth: 'fact'` answers and stops. `depth: 'brief'` leads with the answer,
 * then at most `maxDrivers` drivers, then a so-what (only from engine
 * findings, never model arithmetic), then the named caveats.
 */
export type AskStorylineLede =
  | 'figure_with_scope'
  | 'largest_and_gap'
  | 'from_to_change'
  | 'answer_then_drivers'
  | 'direction_and_size'
  | 'options_then_recommendation'
  | 'position_against_target'
  | 'concentration';

export type AskStorylineCaveat =
  | 'coverage_gap'
  | 'provenance_mix'
  | 'non_additive_members'
  | 'missing_owner';

export type AskStorylineContract = Readonly<{
  depth: 'fact' | 'brief';
  lede: AskStorylineLede;
  maxDrivers: 0 | 1 | 2 | 3;
  soWhat: boolean;
  caveats: readonly AskStorylineCaveat[];
}>;

const FACT = { depth: 'fact', maxDrivers: 0, soWhat: false } as const;
const BRIEF = { depth: 'brief', maxDrivers: 3, soWhat: true } as const;

export const ASK_EXHIBIT_STORYLINES: Readonly<Record<AskExhibitKind, AskStorylineContract>> = {
  kpi: { ...FACT, lede: 'figure_with_scope', caveats: ['coverage_gap'] },
  ranked_bars: { ...FACT, lede: 'largest_and_gap', maxDrivers: 1, caveats: ['coverage_gap'] },
  table: { ...FACT, lede: 'figure_with_scope', caveats: ['coverage_gap'] },
  bridge: { ...BRIEF, lede: 'from_to_change', caveats: ['coverage_gap', 'provenance_mix'] },
  trend: { ...BRIEF, lede: 'direction_and_size', maxDrivers: 2, caveats: ['coverage_gap'] },
  decision_table: {
    ...BRIEF,
    lede: 'answer_then_drivers',
    caveats: ['non_additive_members', 'missing_owner', 'provenance_mix'],
  },
  composition: { ...BRIEF, lede: 'concentration', maxDrivers: 2, caveats: ['coverage_gap'] },
  target_gauge: { ...BRIEF, lede: 'position_against_target', caveats: ['coverage_gap'] },
  scenario_cards: { ...BRIEF, lede: 'options_then_recommendation', caveats: ['provenance_mix'] },
  scenario_option_set: {
    ...BRIEF,
    lede: 'options_then_recommendation',
    caveats: ['provenance_mix', 'missing_owner'],
  },
  status_board: {
    ...BRIEF,
    lede: 'position_against_target',
    caveats: ['missing_owner', 'coverage_gap', 'provenance_mix'],
  },
  network: { ...FACT, lede: 'figure_with_scope', caveats: [] },
};

/**
 * WHAT-IF — one exhibit, several ENGINE-COMPUTED variants (spec §9).
 *
 * The engine returns a parameter grid in one answer (e.g. stretch slipping
 * 0..4 quarters); each variant names the bound figures that fill the
 * exhibit's slots at that parameter value, in the same slot order for every
 * variant. The canvas renders a slider over `values` and swaps placeholders;
 * it never computes a variant.
 */
export type AskExhibitWhatIf = Readonly<{
  parameter: Readonly<{ ref: string; label: string; unit: string; values: readonly string[] }>;
  variants: readonly Readonly<{ value: string; figures: readonly string[] }>[];
}>;

export type AskDeclaredExhibitDto = Readonly<{
  schemaVersion: 'ask_declared_exhibit_v1';
  exhibit: AskDeclaredExhibitBody;
  storyline: AskStorylineContract;
  whatIf?: AskExhibitWhatIf;
  // docs/specs/ask-mbb-storyline-v1.md §3.2/S1 — the phrased lede, composed by
  // `composeAskStory` from this declaration and the same bindings. Absent
  // until a slice composes a story for the exhibit's kind (S1: kpi only).
  story?: AskStoryline;
  /** Drawing ratios per placeholder (#8842 option A); never rendered as text. */
  geometry?: AskExhibitGeometry;
  /**
   * The period and scope every exhibit figure shares, stated ONCE in the
   * header (founder 2026-09-25); null for a field the figures do not share.
   */
  context?: Readonly<{ period: string | null; scope: string | null }>;
  /**
   * Placeholders computed by a scenario/model operation. Only these may carry
   * a "modeled" label; base data never does (founder 2026-09-25 01:39 PT).
   */
  modeledFigures?: readonly string[];
}>;

/**
 * Everything wrong with a what-if, named; empty means publishable. A server
 * publishes a what-if only when this is empty against the placeholders it
 * actually published as bindings.
 */
export function askWhatIfProblems(
  whatIf: AskExhibitWhatIf, publishedPlaceholders: ReadonlySet<string>,
): readonly string[] {
  const values = new Set(whatIf.parameter.values);
  const covered = new Set(whatIf.variants.map((variant) => variant.value));
  const slots = whatIf.variants[0]?.figures.length ?? 0;
  return [
    ...whatIf.variants
      .filter((variant) => !values.has(variant.value))
      .map((variant) => `variant ${variant.value} is not a parameter value`),
    ...whatIf.parameter.values
      .filter((value) => !covered.has(value))
      .map((value) => `parameter value ${value} has no variant`),
    ...whatIf.variants
      .filter((variant) => variant.figures.length !== slots)
      .map((variant) => `variant ${variant.value} has ${variant.figures.length} figures, expected ${slots}`),
    ...whatIf.variants.flatMap((variant) => variant.figures
      .filter((figure) => !publishedPlaceholders.has(figure))
      .map((figure) => `variant ${variant.value} names unbound figure ${figure}`)),
  ];
}

export type AskExhibitDeclarationInput = Readonly<{
  outputs: readonly AskHelixRequestedOutputOutcomeDto[] | null | undefined;
  numericBindings: readonly AskHelixNumericBindingDto[] | null | undefined;
}>;

type Operation = AskHelixRequestedOutputOutcomeDto['requested']['operation'];
type Computed = Readonly<{
  outputId: string;
  operation: Operation;
  dependsOn: readonly string[];
  /** #8861: the governed measure label and population key, from the ledger. */
  label?: string;
  populationKey?: string;
  groupBy?: readonly string[];
  bindings: readonly (AskHelixNumericBindingDto & Partial<RuntimeV3ClauseCarrier>)[];
}>;

// scenario_estimate: a one-figure scenario answer is a KPI too; its figure is the
// only kind that carries a "modeled" label (modeledFigures, founder 2026-09-25).
const KPI_OPERATIONS: ReadonlySet<Operation> = new Set<Operation>(['total', 'count', 'scenario_estimate']);
const RANKED_OPERATIONS: ReadonlySet<Operation> = new Set<Operation>(['by_member', 'rank']);
const CHANGE_OPERATIONS: ReadonlySet<Operation> = new Set<Operation>(['subtract', 'period_over_period']);
const MAX_KPI_FIGURES = 4;
/** A change reconciles when it equals to − from within half a cent. */
const RECONCILE_TOLERANCE = 0.005;

export function declareAskExhibit(input: AskExhibitDeclarationInput): AskDeclaredExhibitDto | null {
  // B1 (review HOLD @ 8161b846): a partial answer's "not available" notice lives only in
  // answerMarkdown, not beside an exhibit, so ANY uncomputed requested output declares nothing.
  if ((input.outputs ?? []).some((output) => output.state !== 'computed')) return null;
  const computed = computedOutputs(input);
  if (computed.length === 0) return null;
  const exhibit = declareBridge(computed)
    ?? declarePeriodTable(computed)
    ?? declareMeasureTable(computed)
    ?? declareDecisionTable(computed)
    ?? declareSingle(computed)
    ?? declareKpi(computed);
  if (exhibit === null) return null;
  const storyline = ASK_EXHIBIT_STORYLINES[exhibit.kind];
  return { schemaVersion: 'ask_declared_exhibit_v1', exhibit, storyline };
}

function computedOutputs(input: AskExhibitDeclarationInput): readonly Computed[] {
  const bindings = input.numericBindings ?? [];
  return (input.outputs ?? [])
    .filter((output) => output.state === 'computed')
    .map((output) => ({
      outputId: output.outputId,
      operation: output.requested.operation,
      dependsOn: output.requested.dependsOn,
      label: output.requested.label,
      populationKey: output.requested.populationKey,
      groupBy: output.requested.groupBy,
      bindings: bindings.filter((binding) => binding.outputId === output.outputId),
    }))
    .filter((output) => output.bindings.length > 0);
}

/** subtract(a, b) = a − b: the bridge runs from b to a, and must reconcile. */
function declareBridge(computed: readonly Computed[]): AskDeclaredExhibitBody | null {
  if (computed.length !== 3) return null;
  const change = computed.find((output) => CHANGE_OPERATIONS.has(output.operation));
  if (!change || change.dependsOn.length !== 2 || change.bindings.length !== 1) return null;
  const [minuend, subtrahend] = change.dependsOn.map((id) => soleBinding(computed, id));
  if (!minuend || !subtrahend || !reconciles(subtrahend, minuend, change.bindings[0])) return null;
  const [fromLabel, toLabel] = distinguishingLabels(subtrahend, minuend);
  return {
    kind: 'bridge',
    subject: subjectOf(subtrahend),
    from: { ...labelledFigure(subtrahend), label: fromLabel },
    to: { ...labelledFigure(minuend), label: toLabel },
    change: { label: 'Change', figure: change.bindings[0].placeholder },
  };
}

function soleBinding(
  computed: readonly Computed[], outputId: string,
): AskHelixNumericBindingDto | null {
  const output = computed.find((candidate) => candidate.outputId === outputId);
  return output && output.bindings.length === 1 ? output.bindings[0] : null;
}

function reconciles(
  from: AskHelixNumericBindingDto, to: AskHelixNumericBindingDto, change: AskHelixNumericBindingDto,
): boolean {
  if (from.value === undefined || to.value === undefined || change.value === undefined) return false;
  return Math.abs(to.value - from.value - change.value) <= RECONCILE_TOLERANCE;
}

/** Two or more by_member outputs over the same member labels, one row per member. */
function declareDecisionTable(computed: readonly Computed[]): AskDeclaredExhibitBody | null {
  if (computed.length < 2) return null;
  if (!computed.every((output) => RANKED_OPERATIONS.has(output.operation))) return null;
  const memberSets = computed.map((output) => memberLabels(output.bindings));
  const [first] = memberSets;
  // B3: the rows join by label, so a repeated label would drop or repeat a member.
  if (!memberSets.every((labels) => new Set(labels).size === labels.length)) return null;
  if (!memberSets.every((labels) => sameMembers(labels, first))) return null;
  // B2: a column needs one governed scope shared by its whole output, and the
  // columns must be told apart; a member-named scope gives neither.
  if (!computed.every((output) => sharedScope(output.bindings) !== null)) return null;
  const leads = computed.map((output) => output.bindings[0]);
  const columnLabels = distinguishingLabelList(leads);
  if (new Set(columnLabels).size !== columnLabels.length || columnLabels.includes('')) return null;
  const last = computed[computed.length - 1];
  const rowOrder = sortedByValue(last.bindings).map((binding) => memberLabel(binding));
  return {
    kind: 'decision_table',
    subject: sharedScope(leads),
    columns: computed.map((output, index) => ({ outputId: output.outputId, label: columnLabels[index] })),
    rows: rowOrder.map((label) => ({
      label,
      cells: computed.map((output) =>
        output.bindings.find((binding) => memberLabel(binding) === label)?.placeholder ?? null),
    })),
  };
}

/** One output whose own operation names its exhibit. */
function declareSingle(computed: readonly Computed[]): AskDeclaredExhibitBody | null {
  if (computed.length !== 1) return null;
  const [output] = computed;
  // #8861 slice 2: one member is one figure, drawn once as a KPI card.
  if (RANKED_OPERATIONS.has(output.operation) && output.bindings.length === 1
    && output.bindings[0].claimRole === 'row') {
    return { kind: 'kpi', figures: [labelledFigure(output.bindings[0])] };
  }
  if (RANKED_OPERATIONS.has(output.operation) && output.bindings.length > 1) {
    return {
      kind: 'ranked_bars',
      subject: sharedScope(output.bindings),
      members: sortedByValue(output.bindings).map(labelledFigure),
    };
  }
  if (output.operation === 'series') {
    if (output.bindings.some((binding) => binding.claimRole === 'row')) return null;
    const points = output.bindings.filter((binding) => binding.claimRole !== 'disclosure');
    const changes = output.bindings.filter((binding) => binding.claimRole === 'disclosure'
      && binding.periodComparison?.operation === 'subtract');
    return {
      kind: 'trend',
      subject: sharedScope(points),
      ...(changes.length === 1 ? { change: { label: 'Change', figure: changes[0].placeholder } } : {}),
      points: [...points].sort(byPeriodStart).map((binding) => ({
        label: binding.timeframe.label ?? memberLabel(binding),
        figure: binding.placeholder,
      })),
    };
  }
  return null;
}

/**
 * One to four headline figures, each the single non-disclosure figure of a
 * total or count output; the engine's disclosure-role figures beside them form
 * a separate signed group (claimRole, never label text).
 */
function declareKpi(computed: readonly Computed[]): AskDeclaredExhibitBody | null {
  if (!computed.every((output) => KPI_OPERATIONS.has(output.operation))) return null;
  const leads = computed.map((output) =>
    output.bindings.filter((binding) => binding.claimRole !== 'disclosure'));
  if (!leads.every((own) => own.length === 1) || leads.length > MAX_KPI_FIGURES) return null;
  const disclosed = computed.flatMap((output) =>
    output.bindings.filter((binding) => binding.claimRole === 'disclosure'));
  const figures = leads.map(([lead]) => labelledFigure(lead));
  if (disclosed.length === 0) return { kind: 'kpi', figures };
  // Lead ruling 09-25: a $0 disclosure tells the reader nothing as a row.
  const zeros = disclosed.filter((binding) => binding.value === 0);
  const nonZero = disclosed.filter((binding) => binding.value !== 0);
  return {
    kind: 'kpi',
    figures,
    disclosures: sortedByValue(nonZero).map((binding) => ({
      ...labelledFigure(binding), direction: directionOf(binding.value),
    })),
    ...(zeros.length > 0 ? { zeroDisclosures: zeros.map(labelledFigure) } : {}),
  };
}

function directionOf(value: number | undefined): 'gain' | 'loss' | 'neutral' {
  if (value === undefined || value === 0) return 'neutral';
  return value > 0 ? 'gain' : 'loss';
}

function labelledFigure(binding: AskHelixNumericBindingDto): AskExhibitFigure {
  return { label: memberLabel(binding), figure: binding.placeholder,
    ...(binding.timeframe.periodBasis ? { periodBasis: binding.timeframe.periodBasis } : {}) };
}

function memberLabel(binding: AskHelixNumericBindingDto): string {
  return binding.displayLabel ?? binding.scope.label;
}

function memberLabels(bindings: readonly AskHelixNumericBindingDto[]): readonly string[] {
  return bindings.map(memberLabel);
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  const set = new Set(b);
  return a.length === b.length && a.every((label) => set.has(label));
}

function sortedByValue(
  bindings: readonly AskHelixNumericBindingDto[],
): readonly AskHelixNumericBindingDto[] {
  return [...bindings].sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));
}

function byPeriodStart(a: AskHelixNumericBindingDto, b: AskHelixNumericBindingDto): number {
  return (a.timeframe.start ?? '').localeCompare(b.timeframe.start ?? '');
}

/** A bridge's subject: the governed measure label its figures share. */
function subjectOf(binding: AskHelixNumericBindingDto): string {
  return memberLabel(binding);
}

/**
 * The scope every binding shares, or null. Null also when that scope is a
 * member's own name (production member rows carry scope.label ===
 * displayLabel, review B2), so a member is never promoted to a subject.
 */
function sharedScope(bindings: readonly AskHelixNumericBindingDto[]): string | null {
  const scopes = new Set(bindings.map((binding) => binding.scope.label));
  if (scopes.size !== 1) return null;
  const [scope] = scopes;
  return bindings.some((binding) => memberLabel(binding) === scope) ? null : scope;
}

/**
 * B4 (review HOLD @ 8161b846): everything wrong with an option set, named;
 * empty is the only publishable state. Other exhibit kinds have no options.
 */
export function askOptionSetProblems(
  exhibit: AskDeclaredExhibitBody, publishedPlaceholders: ReadonlySet<string>,
): readonly string[] {
  if (exhibit.kind !== 'scenario_option_set') return [];
  const seen = new Set<string>();
  const perOption = exhibit.options.flatMap((option) => {
    const problems: string[] = [];
    if (option.figures.length === 0) problems.push(`option ${option.optionRef} has no figures`);
    if (seen.has(option.optionRef)) problems.push(`option ${option.optionRef} is declared more than once`);
    seen.add(option.optionRef);
    for (const figure of option.figures) {
      if (!publishedPlaceholders.has(figure.figure)) {
        problems.push(`option ${option.optionRef} names unbound figure ${figure.figure}`);
      }
    }
    return problems;
  });
  const plan = exhibit.planFigures
    .filter((figure) => !publishedPlaceholders.has(figure.figure))
    .map((figure) => `plan names unbound figure ${figure.figure}`);
  return [
    ...(exhibit.options.length === 0 ? ['option set has no options'] : []),
    ...perOption,
    ...plan,
    ...repeatedPlacements(exhibit),
  ];
}

/** Each placeholder may fill one slot in the whole exhibit (options + plan). */
function repeatedPlacements(
  exhibit: Extract<AskDeclaredExhibitBody, { kind: 'scenario_option_set' }>,
): readonly string[] {
  const placed = [
    ...exhibit.options.flatMap((option) => option.figures.map((figure) => figure.figure)),
    ...exhibit.planFigures.map((figure) => figure.figure),
  ];
  const repeated = placed.filter((figure, index) => placed.indexOf(figure) !== index);
  return [...new Set(repeated)].map((figure) => `figure ${figure} is placed more than once`);
}

function distinguishingLabels(
  a: AskHelixNumericBindingDto, b: AskHelixNumericBindingDto,
): [string, string] {
  const [first, second] = distinguishingLabelList([a, b]);
  return [first, second];
}

/**
 * What tells the figures apart, in governed text: the period when periods
 * differ, else the scope with its shared leading segments removed
 * ("Phoenix acquisition, base" / "Phoenix acquisition, stretch" → "base" /
 * "stretch"). Equality tests only; no label is matched against a phrase.
 */
function distinguishingLabelList(bindings: readonly AskHelixNumericBindingDto[]): readonly string[] {
  const periods = bindings.map((binding) => binding.timeframe.label ?? '');
  if (new Set(periods).size === bindings.length) return periods;
  const segments = bindings.map((binding) => binding.scope.label.split(', '));
  const shared = sharedLeadingSegments(segments);
  return segments.map((parts) => parts.slice(shared).join(', ') || parts.join(', '));
}

function sharedLeadingSegments(segments: readonly (readonly string[])[]): number {
  const shortest = Math.min(...segments.map((parts) => parts.length));
  let shared = 0;
  while (shared < shortest && segments.every((parts) => parts[shared] === segments[0][shared])) {
    shared += 1;
  }
  return shared;
}

export { askKpiGroupProblems, askPlaceholderKey } from './declared-exhibit-groups';
export type { AskKpiGroup } from './declared-exhibit-groups';
